import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Chip,
  Stack,
  Alert,
  useTheme,
  ToggleButtonGroup,
  ToggleButton,
  Divider,
  CircularProgress
} from '@mui/material';
import {
  LocalOffer as TagIcon,
  Article as ArticleIcon,
  Collections as CollectionIcon
} from '@mui/icons-material';
import api from '../../../../services/api';
import PlatformDialog from '../../../molecules/PlatformDialog/PlatformDialog';
import PlatformDialogTitle from '../../../molecules/PlatformDialog/PlatformDialogTitle';

const CollectionModal = ({
  open,
  onClose,
  collection,
  onCreateCollection,
  onSaveCollection
}) => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'tags',
    tags: [],
    articles: []
  });
  const [availableTags, setAvailableTags] = useState([]);
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingTags, setLoadingTags] = useState(false);

  // Carregar tags disponíveis
  useEffect(() => {
    if (open) {
      loadAvailableTags();
    }
  }, [open]);

  // Preencher form quando editando
  useEffect(() => {
    if (open) {
      if (collection) {
        setFormData({
          name: collection.name || '',
          description: collection.description || '',
          type: collection.type || 'tags',
          tags: collection.tags || [],
          articles: collection.articles || []
        });
        // Extrair IDs das tags
        const tagIds = (collection.tags || []).map(t => t._id || t);
        setSelectedTagIds(tagIds);
      } else {
        setFormData({
          name: '',
          description: '',
          type: 'tags',
          tags: [],
          articles: []
        });
        setSelectedTagIds([]);
      }
      setError(null);
    }
  }, [open, collection]);

  const loadAvailableTags = async () => {
    try {
      setLoadingTags(true);
      const res = await api.get('/tags');
      setAvailableTags(res.data.data || []);
    } catch (err) {
      console.error('Erro ao carregar tags:', err);
    } finally {
      setLoadingTags(false);
    }
  };

  const handleTypeChange = (_, newType) => {
    if (newType) {
      setFormData(prev => ({ ...prev, type: newType }));
      // Limpar seleções ao mudar tipo
      setSelectedTagIds([]);
    }
  };

  const handleTagToggle = (tagId) => {
    setSelectedTagIds(prev => {
      if (prev.includes(tagId)) {
        return prev.filter(id => id !== tagId);
      }
      return [...prev, tagId];
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError('O nome da coleção é obrigatório');
      return;
    }

    if (formData.type === 'tags' && selectedTagIds.length === 0) {
      setError('Selecione pelo menos uma tag para a coleção');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const collectionData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        type: formData.type,
        tags: formData.type === 'tags' ? selectedTagIds : [],
        articles: formData.type === 'articles' ? formData.articles : []
      };

      if (collection) {
        // Editando
        await onSaveCollection?.({ ...collectionData, _id: collection._id });
      } else {
        // Criando
        await onCreateCollection?.(collectionData);
      }

      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao salvar coleção');
      console.error('Erro ao salvar coleção:', err);
    } finally {
      setLoading(false);
    }
  };

  const isEditing = !!collection;

  return (
    <PlatformDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <PlatformDialogTitle
          title={isEditing ? 'Editar Coleção' : 'Nova Coleção'}
          subtitle={isEditing ? 'Atualize as configurações desta coleção' : 'Crie um novo agrupamento de artigos'}
          icon={<CollectionIcon />}
          onClose={onClose}
        />
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ mt: 2 }}>
            {/* Nome */}
            <TextField
              autoFocus
              label="Nome da Coleção"
              fullWidth
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              error={!!error && !formData.name.trim()}
              sx={{ mb: 2 }}
            />

            {/* Descrição */}
            <TextField
              label="Descrição (opcional)"
              fullWidth
              multiline
              rows={2}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              sx={{ mb: 3 }}
            />

            <Divider sx={{ mb: 2 }} />

            {/* Tipo de coleção */}
            <Typography variant="subtitle2" gutterBottom>
              Tipo de Coleção
            </Typography>
            <ToggleButtonGroup
              value={formData.type}
              exclusive
              onChange={handleTypeChange}
              fullWidth
              sx={{ mb: 3 }}
            >
              <ToggleButton value="tags" sx={{ py: 1.5 }}>
                <TagIcon sx={{ mr: 1 }} />
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="body2" fontWeight={600}>
                    Coleção de Tags
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Agrupa automaticamente artigos das tags selecionadas
                  </Typography>
                </Box>
              </ToggleButton>
              <ToggleButton value="articles" sx={{ py: 1.5 }}>
                <ArticleIcon sx={{ mr: 1 }} />
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="body2" fontWeight={600}>
                    Coleção de Artigos
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Adicione artigos manualmente à coleção
                  </Typography>
                </Box>
              </ToggleButton>
            </ToggleButtonGroup>

            {/* Seleção de Tags (apenas para tipo 'tags') */}
            {formData.type === 'tags' && (
              <>
                <Typography variant="subtitle2" gutterBottom>
                  Selecione as Tags ({selectedTagIds.length} selecionadas)
                </Typography>

                {loadingTags ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : availableTags.length === 0 ? (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Nenhuma tag disponível. Crie tags primeiro.
                  </Alert>
                ) : (
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      flexWrap: 'wrap',
                      gap: 1,
                      maxHeight: 200,
                      overflowY: 'auto',
                      p: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1
                    }}
                  >
                    {availableTags.map(tag => (
                      <Chip
                        key={tag._id}
                        label={`${tag.name} (${tag.articlesCount || 0})`}
                        onClick={() => handleTagToggle(tag._id)}
                        color={selectedTagIds.includes(tag._id) ? 'primary' : 'default'}
                        variant={selectedTagIds.includes(tag._id) ? 'filled' : 'outlined'}
                        sx={{ m: 0.5 }}
                      />
                    ))}
                  </Stack>
                )}
              </>
            )}

            {/* Info para tipo 'articles' */}
            {formData.type === 'articles' && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Após criar a coleção, você poderá adicionar artigos a ela através do menu de cada artigo.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={onClose}
            disabled={loading}
            variant="outlined"
            sx={{
              borderColor: theme.palette.grey[500],
              color: theme.palette.grey[700],
              '&:hover': {
                backgroundColor: theme.palette.grey[100],
                borderColor: theme.palette.grey[600]
              }
            }}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || (formData.type === 'tags' && selectedTagIds.length === 0)}
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
                boxShadow: theme.shadows[3]
              }
            }}
          >
            {loading ? 'Salvando...' : (isEditing ? 'Salvar' : 'Criar')}
          </Button>
        </DialogActions>
      </form>
    </PlatformDialog>
  );
};

CollectionModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  collection: PropTypes.shape({
    _id: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    type: PropTypes.oneOf(['tags', 'articles']),
    tags: PropTypes.array,
    articles: PropTypes.array
  }),
  onCreateCollection: PropTypes.func,
  onSaveCollection: PropTypes.func
};

export default CollectionModal;

